<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLayoutStore, type ScreenKey } from '@/stores/layout'

const props = withDefaults(
  defineProps<{
    variant?: 'judge' | 'product-edit'
    totalCount?: number
  /** 実際に画面へ出ている件数(LIMITで打ち切られた結果) */
  shownCount?: number
  }>(),
  { variant: 'judge' },
)

const auth = useAuthStore()
const router = useRouter()
const layout = useLayoutStore()

// 左ペインの表示状態は画面ごとに別で保持する
const screenKey = computed<ScreenKey>(() => (props.variant === 'product-edit' ? 'productEdit' : 'judge'))
const isLeftVisible = computed(() => layout.leftPaneVisible[screenKey.value])
/** カードの並び順・拡大画像サイズ・一覧の列幅をまとめて既定に戻す */
function resetLayout() {
  layout.resetCardOrder()
  layout.resetPreviewRatio()
  layout.resetColumnWidths()
}

const leftPaneItems = computed(() =>
  props.variant === 'product-edit'
    ? '検索・NGワードチェック'
    : '検索・商品一覧・NGワードリスト・セラー情報',
)

const title = computed(() => (props.variant === 'product-edit' ? 'セルモン商品編集' : 'セルモン商品審査'))
const subtitle = computed(() =>
  props.variant === 'product-edit' ? 'SalMon Product Editor' : 'SalMon Product Judgment',
)
</script>

<template>
  <div class="topbar" :class="{ 'topbar--product-edit': variant === 'product-edit' }">
    <div class="brandmark" title="トップ画面に戻る" @click="router.push('/')">
      <div class="logo">
        <!-- トップ画面(LandingView)のタイルアイコンと同じ図柄にする。
             審査=チェックマーク、編集=鉛筆。背景のグラデーションもタイルと共通。 -->
        <svg v-if="variant === 'product-edit'" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 11l3 3L22 4"/>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
      </div>
      <div>{{ title }}<div class="sub">{{ subtitle }}</div></div>
    </div>
    <div class="topbar-divider"></div>
    <!-- 左ペインの表示切り替え。畳むと商品情報を全幅で読めるようにする -->
    <button
      class="pane-toggle"
      :class="{ off: !isLeftVisible }"
      :title="`${isLeftVisible ? '左ペインを隠す' : '左ペインを表示'}(${leftPaneItems})`"
      @click="layout.toggleLeftPane(screenKey)"
    >
      <svg class="icon icon-sm" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="16" rx="2"/>
        <line x1="9" y1="4" x2="9" y2="20"/>
      </svg>
      {{ isLeftVisible ? '左ペインを隠す' : '左ペインを表示' }}
    </button>

    <!-- カードの並び順とサイズを既定に戻す。
         並べ替えた結果が分からなくなったときの復帰手段。 -->
    <button
      v-if="variant === 'judge'"
      class="pane-toggle"
      title="カードの並び順・拡大画像の大きさ・一覧の列幅を既定に戻します"
      @click="resetLayout"
    >
      <svg class="icon icon-sm" viewBox="0 0 24 24">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
      </svg>
      デフォルトに戻す
    </button>
    <div class="topbar-divider"></div>
    <div v-if="variant === 'judge'" class="topbar-stats">
      対象 <b>{{ (totalCount ?? 0).toLocaleString() }}</b> 件
      <!-- 一覧はサーバー側で打ち切られるため、総件数と表示件数が食い違うことを明示する -->
      <span v-if="shownCount !== undefined && totalCount !== undefined && shownCount < totalCount" class="topbar-stats-sub">
        （表示は先頭 {{ shownCount.toLocaleString() }} 件）
      </span>
    </div>
    <div class="topbar-spacer"></div>
    <div class="op-chip" v-if="auth.isAuthenticated">
      <span class="dot"></span> {{ auth.displayName }}
    </div>
    <button
      v-if="auth.isAuthenticated"
      class="btn btn-icon topbar-icon-btn"
      title="ログアウト"
      @click="auth.logout()"
    >
      <svg class="icon" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
    </button>
  </div>
</template>
